import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_NAV, type NavItem, loadLanguage, getAvailableLanguages, RTL_LANGUAGES, getCurrentLanguage } from '@school/shared';
import { Icon, Avatar, Dropdown, Spinner } from '@school/ui-lib';
import adminApi from '../api/client';
import './AdminLayout.css';
import { uploadUrl } from '../utils/uploadUrl';

export function AdminLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem('sidebar-collapsed') === '1');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  // Track module updates reactively so sidebar re-renders when modules are toggled
  const [modVersion, setModVersion] = useState(0);

  // Get user info from token
  const userStr = localStorage.getItem('auth_user');
  const user = userStr ? JSON.parse(userStr) : { name: 'Admin', role: 'Admin' };

  // Load RBAC permissions & active modules from localStorage
  // These are set by Login.tsx after successful login
  const permStr = localStorage.getItem('auth_permissions');
  const modStr = localStorage.getItem('auth_modules');
  const userPermissions: Set<string> = new Set(permStr ? JSON.parse(permStr) : []);
  const activeModules: Set<string> = new Set(modStr ? JSON.parse(modStr) : []);
  const isSuperAdmin = user.role?.toLowerCase() === 'super admin';

  // Listen for modules-updated event from ModuleSettings page
  useEffect(() => {
    const handler = () => setModVersion(v => v + 1);
    window.addEventListener('modules-updated', handler);
    return () => window.removeEventListener('modules-updated', handler);
  }, []);

  // ─── Visibility Check: Module gate + RBAC gate ───
  // PHP sidebar.php order:
  //   1. module_lib->hasActive('xxx')  → applies to ALL users (even Super Admin)
  //   2. rbac->hasPrivilege('xxx', 'can_view') → Super Admin bypasses THIS only
  function canSeeItem(item: NavItem): boolean {
    // Dashboard is always visible
    if (item.key === 'dashboard') return true;

    // ── Step 1: Module activation check (applies to ALL users, even Super Admin) ──
    // PHP: if ($this->module_lib->hasActive('front_cms')) { ... }
    // Module_lib does NOT have a Super Admin bypass — it's a simple boolean check
    if (item.module && !activeModules.has(item.module)) return false;

    // ── Step 2: RBAC permission check (Super Admin bypasses this) ──
    // PHP Rbac.php line 54: if ($logged_user_role == 'Super Admin') return true;
    if (isSuperAdmin) return true;

    // Super Admin only items (PHP: hasPrivilege('superadmin') — only passes via SA bypass)
    if (item.superAdminOnly) return false;

    // Check permissions (default action is 'can_view')
    if (item.permissions && item.permissions.length > 0) {
      const action = item.permissionAction || 'can_view';
      // ANY matching permission = show (same as PHP OR logic in sidebar.php)
      return item.permissions.some(perm => userPermissions.has(`${perm}-${action}`));
    }

    // No specific permission required — show it
    return true;
  }

  // Filter nav items recursively
  function filterNav(items: NavItem[]): NavItem[] {
    return items
      .filter(canSeeItem)
      .map(item => {
        if (item.children) {
          const filteredChildren = item.children.filter(canSeeItem);
          // Don't show parent if no children are visible
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter(Boolean) as NavItem[];
  }

  // modVersion triggers re-render when modules are toggled, causing activeModules to refresh from localStorage
  void modVersion;
  const filteredNav = filterNav(ADMIN_NAV);

  // Collapse sidebar on mobile navigation
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Save sidebar state
  useEffect(() => { sessionStorage.setItem('sidebar-collapsed', collapsed ? '1' : ''); }, [collapsed]);

  // ─── Theme Loader — apply saved theme on mount ───
  // Bridges --theme-* vars AND --surface-*/--text-* vars so ALL module pages adapt
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('auth_settings') || localStorage.getItem('settings') || '{}');
      const themeId = stored.theme || 'default.jpg';
      // Theme color map (must match GeneralSettings.tsx THEMES array)
      const THEME_MAP: Record<string, { sidebar: string; header: string; accent: string; bg: string }> = {
        'default.jpg':  { sidebar: '#1e293b', header: '#3c8dbc', accent: '#3c8dbc', bg: '#0f172a' },
        'white.jpg':    { sidebar: '#ffffff', header: '#4361ee', accent: '#4361ee', bg: '#f0f2f5' },
        'red.jpg':      { sidebar: '#1a1225', header: '#dd4b39', accent: '#dd4b39', bg: '#0f0a19' },
        'blue.jpg':     { sidebar: '#0c1929', header: '#0073b7', accent: '#0073b7', bg: '#071018' },
        'gray.jpg':     { sidebar: '#2d2d2d', header: '#555555', accent: '#777777', bg: '#1a1a1a' },
        'indigo.jpg':   { sidebar: '#1e1b4b', header: '#4f46e5', accent: '#6366f1', bg: '#0f0d2e' },
        'emerald.jpg':  { sidebar: '#064e3b', header: '#059669', accent: '#10b981', bg: '#022c22' },
        'violet.jpg':   { sidebar: '#2e1065', header: '#7c3aed', accent: '#8b5cf6', bg: '#1a0940' },
        'amber.jpg':    { sidebar: '#451a03', header: '#d97706', accent: '#f59e0b', bg: '#27100a' },
        'rose.jpg':     { sidebar: '#4c0519', header: '#e11d48', accent: '#f43f5e', bg: '#2a0310' },
        'cyan.jpg':     { sidebar: '#083344', header: '#0891b2', accent: '#06b6d4', bg: '#042027' },
        'slate.jpg':    { sidebar: '#0f172a', header: '#334155', accent: '#64748b', bg: '#020617' },
      };
      const theme = THEME_MAP[themeId] || THEME_MAP['default.jpg'];
      const root = document.documentElement;
      const isLight = themeId === 'white.jpg';

      // 1. Layout theme vars (sidebar, header, bg)
      root.style.setProperty('--theme-sidebar', theme.sidebar);
      root.style.setProperty('--theme-header', theme.header);
      root.style.setProperty('--theme-accent', theme.accent);
      root.style.setProperty('--theme-bg', theme.bg);
      root.style.setProperty('--theme-text', isLight ? '#1e293b' : '#f1f5f9');
      root.style.setProperty('--theme-text-secondary', isLight ? '#475569' : '#94a3b8');
      root.style.setProperty('--theme-border', isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)');
      root.style.setProperty('--theme-surface', isLight ? '#ffffff' : '#1e293b');

      // 2. Bridge to --surface-*/--text-* vars used by Modules.css and tokens.css
      //    This ensures ALL module page content (cards, tables, forms) adapts to theme
      if (isLight) {
        root.setAttribute('data-theme', 'light');
        root.style.setProperty('--surface-bg', '#f0f2f5');
        root.style.setProperty('--surface-card', '#ffffff');
        root.style.setProperty('--surface-elevated', '#f1f5f9');
        root.style.setProperty('--surface-border', '#e2e8f0');
        root.style.setProperty('--surface-input', '#ffffff');
        root.style.setProperty('--text-primary', '#1e293b');
        root.style.setProperty('--text-secondary', '#64748b');
        root.style.setProperty('--text-muted', '#94a3b8');
        root.style.setProperty('--color-neutral-300', '#334155');
        // Non-standard aliases used by ExamManage.css, OnlineAdmission.css, etc.
        root.style.setProperty('--card-bg', '#ffffff');
        root.style.setProperty('--border-color', '#e2e8f0');
        root.style.setProperty('--bg-hover', 'rgba(67,97,238,0.04)');
        root.style.setProperty('--color-text', '#1e293b');
        root.style.setProperty('--color-bg', '#f8fafc');
        root.style.setProperty('--color-text-primary', '#1e293b');
        root.style.setProperty('--color-border', '#e2e8f0');
        // Interactive
        root.style.setProperty('--hover-bg', 'rgba(67,97,238,0.04)');
        root.style.setProperty('--hover-bg-strong', 'rgba(67,97,238,0.08)');
        root.style.setProperty('--nav-active-bg', 'rgba(67,97,238,0.12)');
        root.style.setProperty('--nav-active-text', '#4361ee');
        root.style.setProperty('--nav-text', '#64748b');
        // Dropdown
        root.style.setProperty('--dropdown-bg', '#ffffff');
        root.style.setProperty('--dropdown-hover', '#f1f5f9');
        root.style.setProperty('--dropdown-border', '#e2e8f0');
        root.style.setProperty('--dropdown-text', '#1e293b');
        root.style.setProperty('--dropdown-shadow', '0 10px 25px rgba(0,0,0,0.12)');
        // Badges
        root.style.setProperty('--badge-success-bg', 'rgba(34,197,94,0.1)');
        root.style.setProperty('--badge-success-text', '#059669');
        root.style.setProperty('--badge-danger-bg', 'rgba(239,68,68,0.08)');
        root.style.setProperty('--badge-danger-text', '#dc2626');
        root.style.setProperty('--badge-warning-bg', 'rgba(245,158,11,0.1)');
        root.style.setProperty('--badge-warning-text', '#d97706');
        root.style.setProperty('--badge-info-bg', 'rgba(67,97,238,0.08)');
        root.style.setProperty('--badge-info-text', '#4361ee');
        // Tables
        root.style.setProperty('--table-header-bg', '#dbeafe');
        root.style.setProperty('--table-row-border', '#f1f5f9');
        root.style.setProperty('--table-row-hover', 'rgba(67,97,238,0.04)');
        // Shadows
        root.style.setProperty('--shadow-card', '0 2px 8px rgba(0,0,0,0.06)');
        root.style.setProperty('--shadow-sm', '0 1px 2px rgba(0,0,0,0.06)');
        root.style.setProperty('--shadow-md', '0 4px 6px rgba(0,0,0,0.08)');
        root.style.setProperty('--shadow-lg', '0 10px 25px rgba(0,0,0,0.1)');
        // Scrollbar
        root.style.setProperty('--scrollbar-thumb', 'rgba(67,97,238,0.2)');
        // Light-mode primary color overrides (indigo → blue)
        root.style.setProperty('--color-primary-300', '#93c5fd');
        root.style.setProperty('--color-primary-400', '#4361ee');
        root.style.setProperty('--color-primary-500', '#3b52cc');
        root.style.setProperty('--color-primary-600', '#2f43a8');
        // Semantic action colors — light theme
        root.style.setProperty('--color-danger-500', '#dc2626');
        root.style.setProperty('--color-danger-bg', 'rgba(239,68,68,0.08)');
        root.style.setProperty('--color-danger-border', 'rgba(239,68,68,0.2)');
        root.style.setProperty('--color-danger-hover', 'rgba(239,68,68,0.15)');
        root.style.setProperty('--color-success-500', '#059669');
        root.style.setProperty('--color-success-bg', 'rgba(34,197,94,0.1)');
        root.style.setProperty('--color-success-border', 'rgba(34,197,94,0.2)');
        root.style.setProperty('--color-success-hover', 'rgba(34,197,94,0.15)');
        root.style.setProperty('--color-info-500', '#2563eb');
        root.style.setProperty('--color-info-bg', 'rgba(37,99,235,0.08)');
        root.style.setProperty('--color-info-border', 'rgba(37,99,235,0.2)');
        root.style.setProperty('--color-info-hover', 'rgba(37,99,235,0.12)');
        root.style.setProperty('--color-warning-500', '#d97706');
        root.style.setProperty('--color-warning-bg', 'rgba(245,158,11,0.1)');
        root.style.setProperty('--color-warning-hover', 'rgba(245,158,11,0.15)');
        root.style.setProperty('--color-primary-bg', 'rgba(67,97,238,0.08)');
        root.style.setProperty('--color-primary-border', 'rgba(67,97,238,0.2)');
        root.style.setProperty('--color-primary-hover', 'rgba(67,97,238,0.12)');
      } else {
        root.removeAttribute('data-theme');
        root.style.setProperty('--surface-bg', theme.bg);
        root.style.setProperty('--surface-card', theme.sidebar);
        root.style.setProperty('--surface-elevated', '#334155');
        root.style.setProperty('--surface-border', 'rgba(255,255,255,0.08)');
        root.style.setProperty('--text-primary', '#f1f5f9');
        root.style.setProperty('--text-secondary', '#94a3b8');
        root.style.setProperty('--text-muted', '#64748b');
        root.style.setProperty('--color-neutral-300', '#cbd5e1');
        // Semantic action colors — dark theme
        root.style.setProperty('--color-danger-500', '#f43f5e');
        root.style.setProperty('--color-danger-bg', 'rgba(244,63,94,0.15)');
        root.style.setProperty('--color-danger-border', 'rgba(244,63,94,0.25)');
        root.style.setProperty('--color-danger-hover', 'rgba(244,63,94,0.25)');
        root.style.setProperty('--color-success-500', '#10b981');
        root.style.setProperty('--color-success-bg', 'rgba(16,185,129,0.15)');
        root.style.setProperty('--color-success-border', 'rgba(16,185,129,0.25)');
        root.style.setProperty('--color-success-hover', 'rgba(16,185,129,0.25)');
        root.style.setProperty('--color-info-500', '#38bdf8');
        root.style.setProperty('--color-info-bg', 'rgba(56,189,248,0.12)');
        root.style.setProperty('--color-info-border', 'rgba(56,189,248,0.25)');
        root.style.setProperty('--color-info-hover', 'rgba(56,189,248,0.2)');
        root.style.setProperty('--color-warning-500', '#facc15');
        root.style.setProperty('--color-warning-bg', 'rgba(250,204,21,0.15)');
        root.style.setProperty('--color-warning-hover', 'rgba(250,204,21,0.2)');
        root.style.setProperty('--color-primary-bg', 'rgba(99,102,241,0.15)');
        root.style.setProperty('--color-primary-border', 'rgba(99,102,241,0.3)');
        root.style.setProperty('--color-primary-hover', 'rgba(99,102,241,0.2)');
        // Primary shade palette — dark theme (indigo)
        root.style.setProperty('--color-primary-300', '#a5b4fc');
        root.style.setProperty('--color-primary-400', '#818cf8');
        root.style.setProperty('--color-primary-500', '#6366f1');
        root.style.setProperty('--color-primary-600', '#4f46e5');
      }
    } catch { /* ignore */ }

    // Listen for theme-changed event (from GeneralSettings page) to apply immediately
    const handleThemeChange = () => {
      // Re-run the same logic by re-mounting
      window.location.reload();
    };
    window.addEventListener('theme-changed', handleThemeChange);
    return () => window.removeEventListener('theme-changed', handleThemeChange);
  }, []);

  // ─── Periodic Theme Sync from DB ───
  // Detects when super admin changes theme in GeneralSettings and auto-applies
  // to all logged-in admin sessions within ~60 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await adminApi.get('/settings/theme');
        const stored = JSON.parse(localStorage.getItem('auth_settings') || localStorage.getItem('settings') || '{}');
        if (data.theme && data.theme !== stored.theme) {
          // Theme changed in DB — update local cache and reload to re-apply
          stored.theme = data.theme;
          localStorage.setItem('auth_settings', JSON.stringify(stored));
          localStorage.setItem('settings', JSON.stringify(stored));
          window.location.reload(); // Reload to re-apply theme via the theme loader above
        }
      } catch { /* silently ignore — non-critical */ }
    }, 60000); // Check every 60 seconds
    return () => clearInterval(interval);
  }, []);

  // Auto-expand parent menu of current route
  useEffect(() => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      for (const item of filteredNav) {
        if (
          (item.path && location.pathname.startsWith(item.path)) ||
          item.children?.some(child => location.pathname.startsWith(child.path || ''))
        ) {
          next.add(item.key);
        }
      }
      return next;
    });
  }, [location.pathname]);

  const toggleExpand = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleLanguageChange = async (langCode: string) => {
    await loadLanguage(langCode);
    setCurrentLang(langCode);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_role');
    localStorage.removeItem('auth_permissions');
    localStorage.removeItem('auth_modules');
    localStorage.removeItem('auth_settings');
    localStorage.removeItem('settings');
    localStorage.removeItem('auth_languages');
    localStorage.removeItem('auth_session');
    localStorage.removeItem('auth_campus');
    navigate('/login');
  };

  const isActive = (path?: string) => path && location.pathname.startsWith(path);

  // ─── Collapsed sidebar flyout state ───
  const [flyoutKey, setFlyoutKey] = useState<string | null>(null);
  const [flyoutPos, setFlyoutPos] = useState(0);
  const [flyoutItem, setFlyoutItem] = useState<NavItem | null>(null);

  // ─── Fixed submenu panel state (for Module Launcher mode) ───
  const [submenuItem, setSubmenuItem] = useState<NavItem | null>(() => {
    try {
      const saved = sessionStorage.getItem('submenu-panel');
      if (saved) {
        const key = saved;
        return filteredNav.find(n => n.key === key) || null;
      }
    } catch {}
    return null;
  });
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const handleSubmenuToggle = (item: NavItem) => {
    if (submenuItem?.key === item.key) {
      // Close if same item clicked
      setSubmenuItem(null);
      sessionStorage.removeItem('submenu-panel');
    } else {
      setSubmenuItem(item);
      sessionStorage.setItem('submenu-panel', item.key);
      // Auto-navigate to dashboard child when opening submenu panel
      const dashChild = item.children?.find(c => c.labelKey === 'dashboard' || c.key.endsWith('_dashboard'));
      if (item.path) {
        navigate(item.path);
      } else if (dashChild?.path) {
        navigate(dashChild.path);
      }
    }
  };

  const closeSubmenu = () => {
    setSubmenuItem(null);
    sessionStorage.removeItem('submenu-panel');
  };

  // Listen for module-launched event from Dashboard module cards
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.moduleKey) {
        setCollapsed(true);
        const navItem = filteredNav.find(n => n.key === detail.moduleKey);
        if (navItem) {
          setSubmenuItem(navItem);
        }
      }
    };
    window.addEventListener('module-launched', handler);
    return () => window.removeEventListener('module-launched', handler);
  }, [filteredNav]);

  const handleFlyout = (item: NavItem, e: React.MouseEvent) => {
    if (!collapsed) return;
    // On desktop, use fixed submenu panel instead of flyout
    if (!isMobile) {
      handleSubmenuToggle(item);
      return;
    }
    // On mobile, use original flyout behavior
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const estimatedHeight = 36 + (item.children?.length || 0) * 36 + 8;
    const viewportH = window.innerHeight;
    let top = rect.top;
    if (top + estimatedHeight > viewportH - 12) {
      top = Math.max(12, viewportH - estimatedHeight - 12);
    }
    setFlyoutPos(top);
    if (flyoutKey === item.key) {
      setFlyoutKey(null); setFlyoutItem(null);
    } else {
      setFlyoutKey(item.key); setFlyoutItem(item);
    }
  };

  // Close flyout on route change (but keep submenu panel open)
  useEffect(() => { setFlyoutKey(null); setFlyoutItem(null); }, [location.pathname]);

  // Helper: find the dashboard child of a nav item
  const getDashboardChild = (item: NavItem): NavItem | undefined =>
    item.children?.find(c => c.labelKey === 'dashboard' || c.key.endsWith('_dashboard'));

  // Helper: get non-dashboard children (hide dashboard from submenu since parent click navigates there)
  const getNonDashboardChildren = (children: NavItem[]): NavItem[] =>
    children.filter(c => c.labelKey !== 'dashboard' && !c.key.endsWith('_dashboard'));

  // Handle parent menu click: navigate to dashboard child (if exists) + toggle expand
  const handleParentClick = (item: NavItem) => {
    const dashChild = getDashboardChild(item);
    if (item.path) {
      navigate(item.path);
    } else if (dashChild?.path) {
      navigate(dashChild.path);
    }
    toggleExpand(item.key);
  };

  const renderNavItem = (item: NavItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedKeys.has(item.key);
    const itemActive = isActive(item.path) || item.children?.some(c => isActive(c.path));

    // ─── Collapsed mode: just render the icon, flyout renders at root level ───
    if (collapsed && hasChildren && depth === 0) {
      return (
        <li key={item.key} className={`nav-item ${itemActive ? 'active' : ''} ${flyoutKey === item.key ? 'flyout-open' : ''}`}>
          <button className="nav-link nav-toggle" onClick={(e) => handleFlyout(item, e)} title={t(item.labelKey)}>
            <Icon name={item.icon || 'folder'} size={18} className={`nav-icon nav-icon-${item.key}`} />
          </button>
        </li>
      );
    }

    // Non-dashboard children for submenu rendering
    const visibleChildren = hasChildren ? getNonDashboardChildren(item.children!) : [];

    return (
      <li key={item.key} className={`nav-item ${itemActive ? 'active' : ''} ${depth > 0 ? 'nav-child' : ''}`}>
        {hasChildren ? (
          <>
            {/* Parent click: navigate to dashboard + toggle submenu */}
            <button className="nav-link nav-toggle" onClick={() => handleParentClick(item)}>
              {depth === 0 && <Icon name={item.icon || 'folder'} size={18} className={`nav-icon nav-icon-${item.key}`} />}
              {depth > 0 && <span className="nav-dot" />}
              <span className="nav-label">{t(item.labelKey)}</span>
              <Icon name={isExpanded ? 'chevron-down' : 'chevron-right'} size={14} className="nav-arrow" />
            </button>
            {isExpanded && visibleChildren.length > 0 && (
              <ul className="nav-sub">
                {visibleChildren.map(child => renderNavItem(child, depth + 1))}
              </ul>
            )}
          </>
        ) : (
          <Link to={item.path || '/'} className="nav-link" title={t(item.labelKey)}>
            {depth === 0 && <Icon name={item.icon || 'folder'} size={18} className={`nav-icon nav-icon-${item.key}`} />}
            {depth > 0 && <span className="nav-dot" />}
            <span className="nav-label">{t(item.labelKey)}</span>
          </Link>
        )}
      </li>
    );
  };

  // ─── Load DB-configured data from localStorage ───

  // Languages: only show languages configured in admin settings
  const langStr = localStorage.getItem('auth_languages');
  const enabledLanguages: Array<{ id: number; language: string; countryCode: string }> = langStr ? JSON.parse(langStr) : [];
  const allLanguages = getAvailableLanguages();
  const displayLanguages = enabledLanguages.length > 0
    ? allLanguages.filter(lang => enabledLanguages.some(el => el.language.toLowerCase() === lang.name.toLowerCase()))
    : allLanguages.slice(0, 5);

  // Session (Academic Year): stored at login from sessions table
  const sessionStr = localStorage.getItem('auth_session');
  const sessionData = sessionStr ? JSON.parse(sessionStr) : { currentSession: { id: 0, session: '' }, allSessions: [] };
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(sessionData.currentSession?.id || 0);
  const canChangeSession = isSuperAdmin || userPermissions.has('quick_session_change-can_view');
  // ─── Campus switcher (OEL-186) — only rendered when the tenant has campuses ───
  const [campusScope, setCampusScope] = useState<{ unrestricted: boolean; campuses: { id: number; name: string; code?: string | null }[]; total_campuses: number } | null>(null);
  const selectedCampus = localStorage.getItem('auth_campus') || '';
  useEffect(() => { adminApi.get('/campus-network/my-scope').then(r => { if (r.data?.success) setCampusScope(r.data.data); }).catch(() => {}); }, []);
  const changeCampus = (v: string) => { if (v) localStorage.setItem('auth_campus', v); else localStorage.removeItem('auth_campus'); window.location.reload(); };

  const canSeeSearch = isSuperAdmin || userPermissions.has('student-can_view') || userPermissions.has('student_search-can_view');

  // ─── Global Student Search Modal ───
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [smQuery, setSmQuery] = useState('');
  const [smResults, setSmResults] = useState<any[]>([]);
  const [smLoading, setSmLoading] = useState(false);
  const smTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const smInputRef = useRef<HTMLInputElement>(null);
  const smAbortRef = useRef<AbortController | null>(null);

  // Keyboard shortcut: Ctrl+K to open (RBAC gated)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (canSeeSearch) setSearchModalOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canSeeSearch]);

  // Auto-focus input when modal opens; cleanup on close
  useEffect(() => {
    if (searchModalOpen) {
      setTimeout(() => smInputRef.current?.focus(), 100);
    } else {
      // Cleanup on close
      setSmQuery(''); setSmResults([]); setSmLoading(false);
      if (smTimerRef.current) { clearTimeout(smTimerRef.current); smTimerRef.current = null; }
      if (smAbortRef.current) { smAbortRef.current.abort(); smAbortRef.current = null; }
    }
  }, [searchModalOpen]);

  // Debounced search with abort controller for race conditions
  const doQuickSearch = useCallback(async (q: string) => {
    // Cancel any in-flight request
    if (smAbortRef.current) smAbortRef.current.abort();

    if (!q.trim()) { setSmResults([]); setSmLoading(false); return; }

    const controller = new AbortController();
    smAbortRef.current = controller;
    setSmLoading(true);
    try {
      const { data } = await adminApi.get('/students/quick-search', {
        params: { q: q.trim() },
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setSmResults(data?.data || []);
        setSmLoading(false);
      }
    } catch (err: any) {
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
        setSmResults([]);
        setSmLoading(false);
      }
    }
  }, []);

  const handleSmQueryChange = (val: string) => {
    setSmQuery(val);
    if (smTimerRef.current) clearTimeout(smTimerRef.current);
    if (!val.trim()) { setSmResults([]); setSmLoading(false); return; }
    smTimerRef.current = setTimeout(() => doQuickSearch(val), 400);
  };

  const handleSmSelect = (id: number) => {
    setSearchModalOpen(false);
    navigate(`/students/${id}`);
  };

  const handleSessionChange = () => {
    // Update localStorage with new session
    const newSession = sessionData.allSessions?.find((s: any) => s.id === selectedSessionId);
    if (newSession) {
      const updated = { ...sessionData, currentSession: newSession };
      localStorage.setItem('auth_session', JSON.stringify(updated));
      setSessionModalOpen(false);
      window.location.reload(); // Reload to apply new session context
    }
  };

  // Header RBAC checks (exactly matching PHP header.php)

  const canSeeLangSwitcher = isSuperAdmin || userPermissions.has('language_switcher-can_view');
  const canSeeCalendar = (isSuperAdmin || userPermissions.has('calendar_to_do_list-can_view')) && activeModules.has('calendar_to_do_list');
  const canSeeAiTutor = activeModules.has('ai_home_tutor');
  const canSeeChat = (isSuperAdmin || userPermissions.has('chat-can_view')) && activeModules.has('chat');

  // Quick Links: only show links the user has permission for
  const quickLinks = [
    { key: 'student_details', label: t('student_details') || 'Student Details', path: '/students/search', perm: 'student-can_view', icon: 'user' },
    { key: 'collect_fees', label: t('collect_fees') || 'Collect Fees', path: '/fees/collect', perm: 'collect_fees-can_view', icon: 'dollar' },
    { key: 'add_income', label: t('add_income') || 'Add Income', path: '/income/add', perm: 'income-can_add', icon: 'dollar' },
    { key: 'add_expense', label: t('add_expense') || 'Add Expense', path: '/expense/add', perm: 'expense-can_view', icon: 'credit-card' },
    { key: 'student_attendance', label: t('student_attendance') || 'Student Attendance', path: '/attendance/student', perm: 'student_attendance-can_view', icon: 'check-circle' },
    { key: 'staff_attendance', label: t('staff_attendance') || 'Staff Attendance', path: '/hr/attendance', perm: 'staff_attendance-can_view', icon: 'check-circle' },
    { key: 'staff_directory', label: t('staff_directory') || 'Staff Directory', path: '/hr/staff', perm: 'staff-can_view', icon: 'users' },
    { key: 'exam_group', label: t('exam_group') || 'Exam Group', path: '/exam/group', perm: 'exam_group-can_view', icon: 'file' },
    { key: 'notice_board', label: t('notice_board') || 'Notice Board', path: '/communication/notice', perm: 'notice_board-can_view', icon: 'bell' },
  ].filter(link => isSuperAdmin || userPermissions.has(link.perm));

  const [quickLinksOpen, setQuickLinksOpen] = useState(false);

  // Load school settings (logos) from localStorage
  const settingsStr = localStorage.getItem('auth_settings');
  const schoolSettings = settingsStr ? JSON.parse(settingsStr) : {};
  const adminLogo = schoolSettings.admin_logo;
  const adminSmallLogo = schoolSettings.admin_small_logo;
  const schoolName = schoolSettings.name || user.schoolName || 'School ERP';
  const apiBase = import.meta.env.VITE_API_URL?.replace('/api', '') || '';

  // ─── Dynamic Favicon + Page Title from General Settings ───
  // PHP header.php line 11: uses admin_small_logo as favicon
  useEffect(() => {
    // Update page title
    document.title = schoolName;

    // Update favicon to admin_small_logo
    if (adminSmallLogo) {
      const faviconUrl = uploadUrl(adminSmallLogo, 'school_content/admin_small_logo');
      let link = document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'shortcut icon';
        document.head.appendChild(link);
      }
      link.href = faviconUrl;

      // Also update the existing favicon link
      const existingFav = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (existingFav) existingFav.href = faviconUrl;
    }
  }, [adminSmallLogo, schoolName, apiBase]);

  return (
    <div className={`admin-layout ${collapsed ? 'sidebar-collapsed' : ''}${submenuItem && !isMobile ? ' submenu-panel-open' : ''}`}>
      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? 'sidebar-mobile-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-logo">
            {collapsed ? (
              adminSmallLogo ? (
                <img src={uploadUrl(adminSmallLogo, 'school_content/admin_small_logo')} alt={schoolName} className="sidebar-logo-img sidebar-logo-small" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <svg className="sidebar-logo-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M32 8L4 24l28 16 28-16L32 8z" fill="var(--logo-cap-fill, #818cf8)" />
                  <path d="M16 32v14c0 2 7.2 8 16 8s16-6 16-8V32L32 44 16 32z" fill="var(--logo-cap-fill, #818cf8)" opacity="0.7" />
                  <rect x="52" y="24" width="3" height="26" rx="1.5" fill="var(--logo-tassel-fill, #c084fc)" />
                  <circle cx="53.5" cy="52" r="3" fill="var(--logo-tassel-fill, #c084fc)" />
                </svg>
              )
            ) : (
              adminLogo ? (
                <img src={uploadUrl(adminLogo, 'school_content/admin_logo')} alt={schoolName} className="sidebar-logo-img sidebar-logo-full" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <>
                  <svg className="sidebar-logo-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M32 8L4 24l28 16 28-16L32 8z" fill="var(--logo-cap-fill, #818cf8)" />
                    <path d="M16 32v14c0 2 7.2 8 16 8s16-6 16-8V32L32 44 16 32z" fill="var(--logo-cap-fill, #818cf8)" opacity="0.7" />
                    <rect x="52" y="24" width="3" height="26" rx="1.5" fill="var(--logo-tassel-fill, #c084fc)" />
                    <circle cx="53.5" cy="52" r="3" fill="var(--logo-tassel-fill, #c084fc)" />
                  </svg>
                  <span className="logo-text">{schoolName}</span>
                </>
              )
            )}
          </Link>
        </div>

        {/* ─── Session Display (Academic Year) — top_sidemenu.php line 1-10 ─── */}
        {!collapsed && sessionData.currentSession?.session && (
          <div className="sidebar-session-bar">
            <div className="session-info" onClick={() => canChangeSession && setSessionModalOpen(true)}>
              <Icon name="calendar" size={14} />
              <span className="session-label">{t('current_session') || 'Session'}: <strong>{sessionData.currentSession.session}</strong></span>
              {canChangeSession && <Icon name="edit" size={12} className="session-edit-icon" />}
            </div>
          </div>
        )}

        {/* ─── Campus in view (OEL-186) ─── */}
        {!collapsed && campusScope && campusScope.total_campuses > 0 && (campusScope.unrestricted || campusScope.campuses.length > 0) && (
          <div className="sidebar-session-bar" title="Campus in view — filters every list and report">
            <div className="session-info" style={{ gap: 6 }}>
              <Icon name="globe" size={14} />
              <select value={selectedCampus} onChange={e => changeCampus(e.target.value)} aria-label="Campus in view"
                style={{ flex: 1, minWidth: 0, background: 'transparent', color: 'inherit', border: 0, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                {campusScope.unrestricted
                  ? <option value="">All campuses</option>
                  : campusScope.campuses.length > 1 && <option value="">My campuses ({campusScope.campuses.length})</option>}
                {campusScope.campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* ─── Quick Links — top_sidemenu.php line 12-105 ─── */}
        {!collapsed && quickLinks.length > 0 && (
          <div className="sidebar-quick-links">
            <button className="quick-links-toggle" onClick={() => setQuickLinksOpen(!quickLinksOpen)}>
              <Icon name="zap" size={14} />
              <span>{t('quick_links') || 'Quick Links'}</span>
              <Icon name={quickLinksOpen ? 'chevron-up' : 'chevron-down'} size={12} className="nav-arrow" />
            </button>
          </div>
        )}

        <nav className="sidebar-nav">
          <ul className="nav-list">
            {filteredNav.map(item => renderNavItem(item))}
          </ul>
        </nav>
        <div className="sidebar-version">{collapsed ? 'v4' : 'v4.0.14-May-2026'}</div>
      </aside>

      {/* Collapsed sidebar flyout — rendered at ROOT level to escape sidebar stacking context */}
      {flyoutKey && flyoutItem && (
        <>
          <div className="nav-flyout-backdrop" onClick={() => { setFlyoutKey(null); setFlyoutItem(null); }} />
          <div className="nav-flyout" style={{ top: flyoutPos }}>
            <div className="nav-flyout-header">{t(flyoutItem.labelKey)}</div>
            {getNonDashboardChildren(flyoutItem.children!).map(child => (
              <Link key={child.key} to={child.path || '/'} className={`nav-flyout-item ${isActive(child.path) ? 'active' : ''}`} onClick={() => { setFlyoutKey(null); setFlyoutItem(null); }}>
                <span className="nav-dot" />
                <span>{t(child.labelKey)}</span>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* ─── Fixed Submenu Panel (desktop only) ─── */}
      {submenuItem && !isMobile && (
        <div className="submenu-panel">
          <div className="submenu-panel-header">
            <span className="submenu-panel-title">{t(submenuItem.labelKey)}</span>
            <button className="submenu-panel-close" onClick={closeSubmenu}>
              <Icon name="x" size={16} />
            </button>
          </div>
          <nav className="submenu-panel-body">
            {getNonDashboardChildren(submenuItem.children?.filter(c => canSeeItem(c)) || []).map(child => (
              <Link
                key={child.key}
                to={child.path || '/'}
                className={`submenu-panel-item ${isActive(child.path) ? 'active' : ''}`}
              >
                <span className="nav-dot" />
                <span>{t(child.labelKey)}</span>
              </Link>
            ))}
          </nav>
        </div>
      )}

      {/* Mobile overlay */}
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Main Content */}
      <div className="main-content">
        {/* Header */}
        <header className="app-header">
          <div className="header-left">
            <button className="header-toggle" onClick={() => {
              if (window.innerWidth < 768) setMobileOpen(!mobileOpen);
              else setCollapsed(!collapsed);
            }}>
              <Icon name="menu" size={20} className="header-icon-menu" />
            </button>
            <span className="header-school-name">{schoolName}</span>
          </div>

          <div className="header-right">
            {/* ─── Student Search Icon (opens modal) ─── */}
            {canSeeSearch && (
              <button className="header-btn" onClick={() => setSearchModalOpen(true)} title={`${t('search_by_student_name') || 'Search Student'} (Ctrl+K)`}>
                <Icon name="search" size={18} className="header-icon-search" />
              </button>
            )}

            {/* ─── Language Switcher — header.php line 158-166 (RBAC gated) ─── */}
            {canSeeLangSwitcher && (
              <Dropdown
                trigger={
                  <button className="header-btn" title={t('language')}>
                    <Icon name="globe" size={18} className="header-icon-globe" />
                    <span className="header-btn-label">{currentLang.charAt(0).toUpperCase() + currentLang.slice(1)}</span>
                  </button>
                }
                items={displayLanguages.map(lang => ({
                  key: lang.code,
                  label: `${lang.nativeName} (${lang.name})`,
                }))}
                onSelect={handleLanguageChange}
                align="right"
              />
            )}

            {/* ─── Calendar — header.php line 170-179 (module + RBAC gated) ─── */}
            {canSeeCalendar && (
              <button className="header-btn" onClick={() => navigate('/calendar')} title={t('calendar')}>
                <Icon name="calendar" size={18} className="header-icon-calendar" />
              </button>
            )}

            {/* ─── AI Home Tutor — visible only when module is enabled ─── */}
            {canSeeAiTutor && (
              <button className="header-btn" onClick={() => navigate('/ai-tutor/analytics')} title="AI Home Tutor">
                <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>🎓</span>
                <span className="header-btn-label">AI Tutor</span>
              </button>
            )}

            {/* ─── Chat — header.php line 220-226 (module + RBAC gated) ─── */}
            {canSeeChat && (
              <button className="header-btn" onClick={() => navigate('/chat')} title={t('chat')}>
                <Icon name="message-square" size={18} className="header-icon-chat" />
              </button>
            )}

            {/* Notifications — Bell with unread badge + dropdown */}
            <NotificationBell navigate={navigate} t={t} />

            {/* User Menu */}
            <Dropdown
              trigger={
                <div className="header-user">
                  <Avatar name={user.name || 'Admin'} size="sm" />
                  <div className="header-user-info">
                    <span className="header-user-name">{user.name}</span>
                    <span className="header-user-role">{user.role}</span>
                  </div>
                </div>
              }
              items={[
                { key: 'profile', label: t('profile') || 'Profile', icon: <Icon name="user" size={16} className="header-icon-user" /> },
                ...(activeModules.has('inventory') ? [{ key: 'my_inventory', label: t('my_inventory') || 'My Inventory', icon: <Icon name="box" size={16} className="header-icon-user" /> }] : []),
                { key: 'password', label: t('change_password') || 'Change Password', icon: <Icon name="lock" size={16} className="header-icon-lock" /> },
                { key: 'divider-1', label: '', divider: true },
                { key: 'logout', label: t('logout') || 'Logout', icon: <Icon name="log-out" size={16} className="header-icon-logout" />, danger: true },
              ]}
              onSelect={(key) => {
                if (key === 'logout') handleLogout();
                else if (key === 'profile') navigate(`/hr/staff/profile/${user.id}`);
                else if (key === 'my_inventory') navigate('/inventory/my-items');
                else if (key === 'password') navigate('/change-password');
              }}
              align="right"
            />
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <Outlet />
        </main>
      </div>

      {/* ─── Global Student Search Modal ─── */}
      {searchModalOpen && (
        <div className="gsm-overlay" onClick={() => setSearchModalOpen(false)} onKeyDown={e => e.key === 'Escape' && setSearchModalOpen(false)}>
          <div className="gsm-modal" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="gsm-header">
              <div className="gsm-title"><Icon name="users" size={20} /> Students Search</div>
              <button className="gsm-close" onClick={() => setSearchModalOpen(false)}><Icon name="x" size={18} /></button>
            </div>

            {/* Search Input */}
            <div className="gsm-input-wrap">
              <label className="gsm-label">Enter student details <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="gsm-input-row">
                <input
                  ref={smInputRef}
                  type="text"
                  className="gsm-input"
                  placeholder="Enter student name, admission no, roll number..."
                  value={smQuery}
                  onChange={e => handleSmQueryChange(e.target.value)}
                  onKeyDown={e => e.key === 'Escape' && setSearchModalOpen(false)}
                />
                {smLoading && <Spinner size="sm" />}
              </div>
            </div>

            {/* Results */}
            <div className="gsm-results">
              {smQuery.trim() && !smLoading && smResults.length > 0 && (
                <div className="gsm-results-header">
                  Search results for "{smQuery}" <span className="gsm-count">{smResults.length}</span>
                </div>
              )}

              {smResults.map((s, idx) => {
                const imgSrc = s.image ? uploadUrl(s.image, 'student_images') : '';
                const hasHostel = !!s.hostel_name;
                return (
                  <div key={s.id} className="gsm-card" onClick={() => handleSmSelect(s.id)}>
                    <div className="gsm-card-photo">
                      {imgSrc ? (
                        <img src={imgSrc} alt="" className="gsm-card-img" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; const sibling = (e.target as HTMLImageElement).nextElementSibling as HTMLElement | null; if (sibling) sibling.style.display = 'flex'; }} />
                      ) : null}
                      <div className="gsm-card-avatar" style={{ display: imgSrc ? 'none' : 'flex', background: s.gender === 'Female' ? '#ec4899' : '#6366f1' }}>
                        {s.name?.charAt(0) || '?'}
                      </div>
                    </div>
                    <div className="gsm-card-info">
                      <div className="gsm-card-name">{idx + 1}. {s.name}</div>
                      <div className="gsm-card-detail">Section: {s.class}<sup>th</sup> - {s.section}</div>
                      {s.roll_no && <div className="gsm-card-detail">Roll number: {s.roll_no}</div>}
                      {s.father_name && <div className="gsm-card-detail">Father name: {s.father_name}{s.father_phone ? ` - ${s.father_phone}` : ''}</div>}
                      {s.mother_name && <div className="gsm-card-detail">Mother name: {s.mother_name}{s.mother_phone ? ` - ${s.mother_phone}` : ''}</div>}
                      {hasHostel && (
                        <div className="gsm-card-detail">Hostel: {s.hostel_name}{s.building_name ? ` - ${s.building_name}` : ''}{s.hostel_floor ? ` → ${s.hostel_floor}` : ''}{s.room_no ? ` → Room ${s.room_no}` : ''}{s.bed_no ? ` → Bed ${s.bed_no}` : ''}</div>
                      )}
                      {hasHostel && s.warden_name && (
                        <div className="gsm-card-detail">Hostel Warden: {s.warden_name}{s.warden_phone ? ` - ${s.warden_phone}` : ''}</div>
                      )}
                    </div>
                    <Icon name="chevron-right" size={16} className="gsm-card-arrow" />
                  </div>
                );
              })}

              {/* Empty state */}
              {smQuery.trim() && !smLoading && smResults.length === 0 && (
                <div className="gsm-empty">
                  <Icon name="search" size={32} />
                  <div>No students found for "{smQuery}"</div>
                </div>
              )}

              {/* Initial state */}
              {!smQuery.trim() && !smLoading && (
                <div className="gsm-empty">
                  <Icon name="search" size={32} />
                  <div>Type to search students</div>
                  <div className="gsm-hint">Search by name, admission number, roll number, or parent name</div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="gsm-footer">
              <button className="gsm-btn-cancel" onClick={() => setSearchModalOpen(false)}>
                <Icon name="x-circle" size={14} /> Cancel
              </button>
              <span className="gsm-shortcut">ESC to close · Ctrl+K to open</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── Session Switch Modal ─── */}
      {sessionModalOpen && (
        <div className="modal-overlay" onClick={() => setSessionModalOpen(false)}>
          <div className="modal-content session-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('switch_session') || 'Switch Academic Session'}</h3>
              <button className="modal-close" onClick={() => setSessionModalOpen(false)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-desc">{t('current_session') || 'Current Session'}: <strong>{sessionData.currentSession?.session}</strong></p>
              <label className="modal-label">{t('select_session') || 'Select Session'}</label>
              <select
                className="modal-select"
                value={selectedSessionId}
                onChange={e => setSelectedSessionId(Number(e.target.value))}
              >
                {sessionData.allSessions?.map((s: any) => (
                  <option key={s.id} value={s.id}>{s.session}</option>
                ))}
              </select>
            </div>
            <div className="modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={() => setSessionModalOpen(false)}>
                {t('cancel') || 'Cancel'}
              </button>
              <button className="modal-btn modal-btn-primary" onClick={handleSessionChange}>
                {t('switch') || 'Switch Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Quick Links Floating Popover (rendered at root level to escape sidebar overflow) ─── */}
      {quickLinksOpen && (
        <>
          <div className="quick-links-backdrop" onClick={() => setQuickLinksOpen(false)} />
          <div className="quick-links-popover">
            <div className="quick-links-popover-header">
              <span>{t('quick_links') || 'Quick Links'}</span>
              <button className="quick-links-close" onClick={() => setQuickLinksOpen(false)}>
                <Icon name="x" size={14} />
              </button>
            </div>
            <div className="quick-links-popover-body">
              {quickLinks.map(link => (
                <Link key={link.key} to={link.path} className="quick-link-item" title={link.label} onClick={() => setQuickLinksOpen(false)}>
                  <Icon name={link.icon} size={16} />
                  <span>{link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Notification Bell Component ───
function NotificationBell({ navigate, t }: { navigate: (p: string) => void; t: (k: string) => string }) {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/notification/unread/count');
      setCount(data?.count || 0);
    } catch { /* ignore */ }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/notification/unread/list');
      setItems(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => clearInterval(timer);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id: number) => {
    try {
      await adminApi.post(`/notification/${id}/read`);
      setCount(c => Math.max(0, c - 1));
      setItems(prev => prev.filter(i => i.id !== id));
    } catch { /* ignore */ }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="header-btn" onClick={() => setOpen(o => !o)} title={t('notification') || 'Notifications'} style={{ position: 'relative' }}>
        <Icon name="bell" size={18} className="header-icon-bell" />
        {count > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2, background: '#ef4444', color: '#fff',
            fontSize: '0.6rem', fontWeight: 700, minWidth: 16, height: 16, borderRadius: 99,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
            lineHeight: 1, border: '2px solid var(--surface-card, #1e293b)',
          }}>
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8,
          background: 'var(--surface-card, #1e293b)', border: '1px solid var(--surface-border, rgba(255,255,255,0.1))',
          borderRadius: 12, width: 340, maxHeight: 400, overflowY: 'auto', zIndex: 1000,
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="bell" size={16} className="header-icon-bell" /> {t('notification') || 'Notifications'}</span>
            {count > 0 && <span style={{ fontSize: '0.7rem', color: 'var(--theme-accent)', fontWeight: 600 }}>{count} {t('unread') || 'unread'}</span>}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--color-success, #34d399)', marginRight: 4, display: 'inline-flex' }}><Icon name="check-circle" size={16} /></span> {t('no_record_found') || 'No new notifications'}
            </div>
          ) : (
            items.map(item => (
              <div
                key={item.id}
                style={{
                  padding: '10px 16px', borderBottom: '1px solid var(--surface-border, rgba(255,255,255,0.04))',
                  cursor: 'pointer', transition: 'background 0.15s',
                }}
                onMouseOver={e => (e.currentTarget.style.background = 'var(--hover-bg, rgba(255,255,255,0.04))')}
                onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                onClick={() => { markRead(item.id); setOpen(false); navigate('/notifications'); }}
              >
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{item.title}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {item.publish_date ? new Date(item.publish_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                </div>
              </div>
            ))
          )}
          <div
            style={{ padding: '10px 16px', textAlign: 'center', borderTop: '1px solid var(--surface-border)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: 'var(--theme-accent)' }}
            onClick={() => { setOpen(false); navigate('/notifications'); }}
          >
            {t('view_all') || 'View All'} →
          </div>
        </div>
      )}
    </div>
  );
}
